<template>
  <div>
    <b-row>
      <!-- Patient Tests Tab -->
      <b-modal
        id="modal-radiology-edit"
        :no-close-on-backdrop="true"
        ok-only
        centered
        size="lg"
        :title="createMode ? 'Add Patient Test' : $t('lab.Edit Patient  Test')"
        @ok="submitItem()"
      >
        <b-card-text>
          <b-row>
            <b-col md="12">
              <b-form-group :label="$t('lab.result')" label-for="URL">
                <b-form-input
                  v-model="EditForm.result"
                  :placeholder="$t('lab.result')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-text>
      </b-modal>
      <DeleteModal modal-id="modal-delete" @deleteToggle="deleteTest" />
      <b-modal
        id="modal-add-discount"
        :no-close-on-backdrop="true"
        ok-only
        centered
        size="lg"
        :title="$t('common.add') + ' ' + $t('common.discount')"
        @ok="addDiscount()"
      >
        <b-card-text>
          <b-row>
            <b-col md="12">
              <b-form-group :label="$t('common.discount')">
                <b-form-input
                  v-model="EditForm.discount"
                  :placeholder="$t('common.discount')"
                />
              </b-form-group>
            </b-col>
          </b-row>
        </b-card-text>
      </b-modal>
      <b-modal
        id="modal-add-test"
        :no-close-on-backdrop="true"
        ok-only
        centered
        size="lg"
        :title="'Add Patient Test'"
        @ok="addTest()"
      >
        <b-card-text>
          <b-row>
            <b-col md="">
              <AutoComplete
                v-model="group"
                label="Test Group"
                placeholder="Select A Group"
                param-query="search"
                :serializer="(val) => val.id"
                endpoint="group"
                text-title="name"
                @input="selectGroup"
              />
            </b-col>
          </b-row>
          <div class="d-flex">
            <div v-for="(item, index) in CreateForm.tests">
              <b-badge class="mx-1" variant="primary">
                {{ item.test.name }}
                <span class="cursor-pointer" @click="removeTest(index)">X</span>
              </b-badge>
            </div>
          </div>
        </b-card-text>
      </b-modal>

      <!-- Modal to show all test items -->
      <b-modal
        id="modal-all-test-items"
        centered
        size="md"
        :title="
          $t('common.all-items') +
          ' - ' +
          $t('common.invoice-number') +
          ' #' +
          selectedInvoiceNo
        "
        hide-footer
      >
        <b-card-text>
          <div v-if="selectedInvoiceItems.length > 0">
            <div
              v-for="(item, index) in selectedInvoiceItems"
              :key="index"
              class="test-item-modal-row"
              :class="{
                'border-bottom': index < selectedInvoiceItems.length - 1,
              }"
            >
              <div
                class="d-flex justify-content-between align-items-center py-2"
              >
                <div class="d-flex align-items-center flex-1">
                  <b-badge
                    variant="primary"
                    class="mr-2"
                    style="font-size: 0.9rem"
                  >
                    {{ index + 1 }}
                  </b-badge>
                  <span class="font-weight-bold text-dark">
                    {{ getNestedProperty(item, "name", "N/A") }}
                  </span>
                </div>
                <div class="ml-3">
                  <span
                    class="text-success font-weight-bolder"
                    style="font-size: 1rem"
                  >
                    {{ formatPrice(getNestedProperty(item, "net_amount", 0)) }}
                  </span>
                </div>
              </div>
            </div>

            <!-- Total -->
            <div class="mt-3 pt-3 border-top">
              <div class="d-flex justify-content-between align-items-center">
                <span
                  class="font-weight-bold text-dark"
                  style="font-size: 1.1rem"
                >
                  {{ $t("common.total") }}:
                </span>
                <span
                  class="text-success font-weight-bolder"
                  style="font-size: 1.2rem"
                >
                  {{ formatPrice(calculateModalTotal()) }}
                </span>
              </div>
            </div>
          </div>
        </b-card-text>
      </b-modal>

      <b-col>
        <b-card>
          <div class="d-flex justify-content-between">
            <h2 class="mb-2">
              {{ $t("lab.Patient Test Details") }}
            </h2>
            <h2 class="mb-2" />
          </div>
          <div class="mb-1">
            <h5>
              {{ $t("common.patient-name") }} :
              {{ getNestedProperty(patientInfo, "patient.name") }}
            </h5>
            <h6>
              {{ $t("lab.gender") }} :
              {{ getNestedProperty(patientInfo, "patient.gender") }}
            </h6>
            <h6>
              {{ $t("common.Birtdate") }} :
              {{ getNestedProperty(patientInfo, "patient.birth_date") }}
            </h6>

            <h6>
              {{ $t("common.phone") }} :
              {{ getNestedProperty(patientInfo, "patient.phone") }}
            </h6>
            <h6>
              {{ $t("emergency.Patient Case") }} :
              {{ getNestedProperty(patientInfo, "patient_case") }}
            </h6>
            <h6>
              {{ $t("common.type") }} :
              {{ getNestedProperty(patientInfo, "type") }}
            </h6>
          </div>
          <div class="d-flex justify-content-end">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mb-75 bgcolor mx-2"
              @click="
                $router.push({
                  name: 'add-test',
                  params: { id: $route.params.id },
                })
              "
              >{{ $t("lab.add-patient-test") }}
            </b-button>
            <tests :data="data" :patient-info="patientInfo" />
          </div>

          <b-tabs content-class="mt-2" @input="onTabChanged">
            <!-- Patient Tests Tab -->
            <b-tab title="Patient Tests" active>
              <b-card>
                <div>
                  <b-table
                    v-if="!loading"
                    :items="data"
                    responsive
                    :fields="tableColumns"
                    primary-key="id"
                    show-empty
                    empty-text="No matching results found"
                    class="position-relative"
                  >
                    <template #cell(sample_id)="data">
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        pill
                        class="mb-75 bgcolor mx-2"
                        @click="editSampleId(data.item)"
                      >
                        {{ getNestedProperty(data.item, "sample_id") }}
                      </b-button>
                    </template>
                    <template #cell(test_name)="data">
                      {{ getNestedProperty(data.item, "test.name") }}
                    </template>
                    <template #cell(unit)="data">
                      {{ getNestedProperty(data.item, "unit.name") }}
                    </template>
                    <template #cell(normalRange)="data">
                      <span class="text-nowrap">
                        {{ getNestedProperty(data.item, "normal_range_max") }} -
                        {{ getNestedProperty(data.item, "normal_range_max") }}
                      </span>
                    </template>
                    <template #cell(state)="data">
                      <div v-if="data.item.sample_id !== null">
                        <b-button
                          v-if="
                            getNestedProperty(data.item, 'is_approved') === 0
                          "
                          variant="success"
                          size="sm"
                          @click="showApproveModal(data.item)"
                        >
                          {{ $t("lab.Approve") }}
                        </b-button>
                        <b-button
                          v-else
                          variant="danger"
                          size="sm"
                          @click="showDisapproveModal(data.item)"
                        >
                          {{ $t("lab.Disapprove") }}
                        </b-button>
                      </div>
                    </template>

                    <template #cell(result)="data">
                      <b-badge
                        v-if="getNestedProperty(data.item, 'result')"
                        :variant="
                          getNestedProperty(data.item, 'is_approved') === 0
                            ? 'danger'
                            : 'success'
                        "
                        class="text-nowrap"
                      >
                        {{ getNestedProperty(data.item, "result") }}
                      </b-badge>
                    </template>

                    <template #cell(actions)="data">
                      <div class="text-nowrap">
                        <!-- @click="printId(data.item.id)" -->
                        <div class="text-nowrap d-flex gap-2">
                          <b-button
                            variant="outline-primary"
                            size="sm"
                            pill
                            class="mb-75 bgcolor mx-2"
                            @click="show(data, 'update')"
                            >{{ $t("lab.Add result") }}
                          </b-button>
                          <ActionsButtons
                            :no-edit="true"
                            delete-perm="delete-surgery"
                            edit-perm="update-surgery"
                            @editToggle="show(data.item)"
                            @deleteToggle="deleteModel(data.item)"
                          />
                        </div>
                        <div class="text-nowrap d-flex gap-2">
                          <!-- <b-button
                            variant="outline-success"
                            size="sm"
                            pill
                            class="mb-75 bgcolor"
                            @click="
                              $router.push({
                                name: 'single-test-details',
                                params: {
                                  id: data.item.test_id,
                                  patientCardId: $route.params.id,
                                },
                              })
                            "
                            >{{ $t("lab.view-test") }}</b-button
                          > -->
                          <!-- <b-button
                            variant="outline-warning"
                            size="sm"
                            pill
                            class="mb-75 bgcolor mx-2"
                            @click="showDiscount(data, 'update')"
                            >{{ $t("lab.add-discount") }}
                          </b-button> -->
                        </div>
                      </div>
                    </template>
                  </b-table>
                  <template v-if="loading">
                    <div>
                      <div class="d-flex justify-content-center mb-1">
                        <b-spinner label="Loading..." />
                      </div>
                    </div>
                  </template>
                  <div class="mx-2 mb-2">
                    <b-row>
                      <b-col
                        cols="12"
                        sm="6"
                        class="d-flex align-items-center justify-content-center justify-content-sm-start"
                      >
                        <span class="text-muted">Showing 1 to 10 of tests</span>
                      </b-col>
                      <!-- Pagination -->
                      <b-col
                        cols="12"
                        sm="6"
                        class="d-flex align-items-center justify-content-center justify-content-sm-end"
                        @click="index()"
                      >
                        <b-pagination
                          v-model="page"
                          :total-rows="dataCount"
                          :per-page="take"
                          first-number
                          last-number
                          class="mb-0 mt-1 mt-sm-0"
                          prev-class="prev-item"
                          next-class="next-item"
                        >
                          <template #prev-text>
                            <feather-icon icon="ChevronLeftIcon" size="18" />
                          </template>
                        </b-pagination>
                      </b-col>
                    </b-row>
                  </div>
                </div>
              </b-card>
            </b-tab>

            <!-- Test Invoices Tab -->
            <b-tab title="Test Invoices">
              <b-card no-body>
                <b-card-header
                  class="d-flex justify-content-between align-items-center"
                >
                  <b-card-title>{{ $t("lab.test-invoices") }}</b-card-title>
                  <b-button
                    variant="primary"
                    size="sm"
                    :disabled="loadingInvoices"
                    @click="getTestInvoices"
                  >
                    <b-spinner v-if="loadingInvoices" small class="mr-1" />
                    {{ $t("common.refresh") }}
                  </b-button>
                </b-card-header>

                <b-card-body>
                  <!-- Search -->
                  <b-row class="mb-1">
                    <b-col md="6">
                      <b-form-group
                        :label="$t('common.search-by-invoice-number')"
                        label-for="invoice-search"
                      >
                        <b-form-input
                          id="invoice-search"
                          v-model="invoiceSearchTerm"
                          :placeholder="$t('common.search-by-invoice-number')"
                        />
                      </b-form-group>
                    </b-col>
                    <b-col md="6">
                      <b-form-group
                        :label="$t('lab.search-by-test-name')"
                        label-for="test-name-search"
                      >
                        <b-form-input
                          id="test-name-search"
                          v-model="testNameSearchTerm"
                          :placeholder="$t('lab.search-by-test-name')"
                        />
                      </b-form-group>
                    </b-col>
                  </b-row>

                  <!-- Test Summary Cards -->
                  <b-row
                    class="mb-2"
                    style="
                      background: #f8f9fa;
                      padding: 0.5rem;
                      border-radius: 0.375rem;
                      border: 1px solid #e9ecef;
                    "
                  >
                    <b-col md="6" class="text-center">
                      <div class="d-flex flex-column align-items-center">
                        <small
                          class="text-muted text-uppercase font-weight-medium mb-1"
                          style="font-size: 0.75rem; letter-spacing: 0.5px"
                          >{{ $t("lab.total-requested-tests") }}</small
                        >
                        <span
                          class="text-primary font-weight-bolder"
                          style="font-size: 1.25rem; line-height: 1.2"
                          >{{ formatCurrency(totalRequestedTests) }}</span
                        >
                      </div>
                    </b-col>
                    <b-col md="6" class="text-center">
                      <div class="d-flex flex-column align-items-center">
                        <small
                          class="text-muted text-uppercase font-weight-medium mb-1"
                          style="font-size: 0.75rem; letter-spacing: 0.5px"
                          >{{ $t("lab.total-approved-tests") }}</small
                        >
                        <span
                          class="text-success font-weight-bolder"
                          style="font-size: 1.25rem; line-height: 1.2"
                          >{{ formatCurrency(totalApprovedTests) }}</span
                        >
                      </div>
                    </b-col>
                  </b-row>

                  <!-- Invoices Table -->
                  <b-table
                    :items="filteredTestInvoices"
                    :fields="invoiceColumns"
                    responsive
                    class="position-relative"
                    :busy="loadingInvoices"
                    show-empty
                    :empty-text="$t('common.no-invoices-found')"
                  >
                    <!-- Custom cells -->
                    <template #cell(invoice_no)="data">
                      <span class="font-weight-bold text-primary">
                        #{{ data.item.invoice_no }}
                      </span>
                    </template>

                    <template #cell(test_items)="data">
                      <div
                        v-if="
                          getNestedProperty(
                            data.item,
                            'invoice_items.length',
                            0
                          ) > 0
                        "
                        class=""
                      >
                        <!-- Show first item only -->
                        <div
                          v-for="(item, index) in data.item.invoice_items.slice(
                            0,
                            1
                          )"
                          :key="index"
                          class="test-item-row"
                        >
                          <div
                            class="d-flex justify-content-between align-items-center py-1"
                          >
                            <div class="test-item-name">
                              <b-badge
                                variant="primary"
                                class="mr-1"
                                style="font-size: 0.85rem"
                              >
                                {{ index + 1 }}
                              </b-badge>
                              <span class="font-weight-bold text-dark">
                                {{ getNestedProperty(item, "name", "N/A") }}
                              </span>
                            </div>
                            <div class="test-item-amount">
                              <span
                                class="text-success font-weight-bolder"
                                style="font-size: 0.95rem"
                              >
                                {{
                                  formatPrice(
                                    getNestedProperty(item, "net_amount", 0)
                                  )
                                }}
                              </span>
                            </div>
                          </div>
                        </div>

                        <!-- Show more button if more than 1 item -->
                        <div v-if="data.item.invoice_items.length > 1" class="">
                          <b-button
                            variant="outline-primary"
                            size="sm"
                            block
                            @click="showAllTestItems(data.item)"
                          >
                            <feather-icon
                              icon="PlusIcon"
                              size="14"
                              class="mr-1"
                            />
                            {{ $t("common.show") }}
                            {{ data.item.invoice_items.length - 1 }}
                            {{ $t("common.more-items") }}
                          </b-button>
                        </div>
                      </div>
                      <span v-else class="text-muted">{{
                        $t("common.no-items")
                      }}</span>
                    </template>

                    <template #cell(amount)="data">
                      <span class="font-weight-bolder text-success">
                        {{ formatPrice(data.item.amount) }}
                      </span>
                    </template>

                    <template #cell(discount)="data">
                      <span class="font-weight-bold text-warning">
                        {{ formatPrice(data.item.discount || 0) }}
                      </span>
                    </template>

                    <template #cell(status)="data">
                      <b-badge
                        :variant="getInvoiceStatusVariant(data.item.status)"
                        class="text-capitalize"
                      >
                        {{ data.item.status }}
                      </b-badge>
                    </template>

                    <template #cell(date)="data">
                      <span class="text-nowrap">
                        {{ formatDate(data.item.date) }}
                      </span>
                    </template>

                    <template #cell(created_at)="data">
                      <span class="text-nowrap text-muted">
                        {{ formatDateTime(data.item.created_at) }}
                      </span>
                    </template>

                    <template #cell(approve_test)="data">
                      <div
                        v-if="
                          data.item.status === 'approved' ||
                          data.item.status === 'printed'
                        "
                      >
                        <b-button
                          v-if="!areAllTestsApproved(data.item)"
                          variant="success"
                          size="sm"
                          @click="approveTestFromInvoice(data.item)"
                        >
                          {{ $t("lab.Approve") }}
                        </b-button>
                        <b-button
                          v-else
                          variant="danger"
                          size="sm"
                          @click="disapproveTestFromInvoice(data.item)"
                        >
                          {{ $t("lab.Disapprove") }}
                        </b-button>
                      </div>
                      <span v-else class="text-muted">
                        {{ $t("lab.invoice-must-be-paid") }}
                      </span>
                    </template>

                    <!-- Loading overlay -->
                    <template #table-busy>
                      <div class="text-center my-2">
                        <b-spinner class="align-middle" />
                        <strong class="ml-2"
                          >{{ $t("common.loading") }}...</strong
                        >
                      </div>
                    </template>
                  </b-table>

                  <!-- Pagination -->
                  <b-row v-if="filteredTestInvoices.length > 0">
                    <b-col md="6" class="d-flex align-items-center">
                      <span class="text-muted">
                        {{ $t("common.showing") }}
                        {{ filteredTestInvoices.length }}
                        {{ $t("common.invoices") }}
                      </span>
                    </b-col>
                    <b-col md="6" class="d-flex justify-content-end">
                      <b-pagination
                        v-model="invoicePage"
                        :total-rows="invoiceTotalRecords"
                        :per-page="invoiceTake"
                        size="sm"
                        @change="getTestInvoices"
                      />
                    </b-col>
                  </b-row>
                </b-card-body>
              </b-card>
            </b-tab>
          </b-tabs>
        </b-card>
      </b-col>
    </b-row>

    <!-- Approve Confirmation Modal -->
    <b-modal
      id="approve-modal"
      title="Approve Test"
      ok-title="Confirm"
      cancel-title="Cancel"
      @ok="approveTest"
    >
      <p>Are you sure you want to approve this test?</p>
    </b-modal>

    <!-- Disapprove Confirmation Modal -->
    <b-modal
      id="disapprove-modal"
      title="Disapprove Test"
      ok-title="Confirm"
      cancel-title="Cancel"
      @ok="disapproveTest"
    >
      <p>Are you sure you want to disapprove this test?</p>
    </b-modal>

    <b-modal
      v-model="editSampleModal"
      title="Edit Sample ID"
      cancel-variant="outline-secondary"
      ok-title="Save"
      @ok.prevent="updateSampleId"
    >
      <b-form>
        <b-form-group label="Sample ID" label-for="sample-id">
          <b-form-input
            id="sample-id"
            v-model="newSampleId"
            placeholder="Enter new sample ID"
            trim
          />
        </b-form-group>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BLink,
  BFormGroup,
  BFormInput,
  BTable,
  BDropdown,
  BBadge,
  BDropdownItem,
  BPagination,
  BSpinner,
  BModal,
  BTabs,
  BTab,
  BCardHeader,
  BCardTitle,
  BCardBody,
} from "bootstrap-vue";
import axios from "axios";
import router from "@/router";
import ReceptionHistoryCard from "@/components/reception/ReceptionHistoryCard";
import ReceptionPatientCardWithQR from "@/components/reception/ReceptionPatientCardWithQR";
import VueHtml2pdf from "vue-html2pdf";
import StatisticCardHorizontal from "@core/components/statistics-cards/StatisticCardHorizontal.vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import ActionsButtons from "@/components/common/ActionsButtons.vue";
import DeleteModal from "@/components/modals/DeleteModal.vue";
import { getNestedProperty } from "@/utils/nestedProperty.js";

const tests = () => import("@/components/printables/Tests.vue");
const AutoComplete = () => import("@/components/common/AutoComplete.vue");

export default {
  components: {
    DeleteModal,
    ActionsButtons,
    BRow,
    BCol,
    BLink,
    BCard,
    BBadge,
    BButton,
    ReceptionHistoryCard,
    ReceptionPatientCardWithQR,
    VueHtml2pdf,
    StatisticCardHorizontal,
    BFormGroup,
    BTable,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BFormInput,
    BModal,
    BTabs,
    BTab,
    BCardHeader,
    BCardTitle,
    BCardBody,
    tests,
    AutoComplete,
  },

  data() {
    return {
      data: {},
      patientCardDetails: {},
      params: {
        form: {},
        path: "",
      },
      EditForm: {
        id: " ",
        normalRange: " ",
        patient_card_id: "",
        price: " ",
        result: " ",
        unit_id: " ",
        resultAsToNormalRange: " ",
        _method: "PUT",
      },
      CreateForm: {
        patient_card_id: "",
        tests: [],
      },
      dataCount: 0,
      patientInfo: {},
      tableColumns: [
        // {
        //   key: "sample_id",
        //   sortable: true,

        //   label: this.$t("lab.sample_id"),
        // },
        {
          key: "test_name",
          sortable: true,

          label: this.$t("common.name"),
        },
        {
          key: "result",
          sortable: true,

          label: this.$t("lab.result"),
        },
        {
          key: "unit",
          sortable: true,

          label: this.$t("lab.unit"),
        },
        {
          key: "normalRange",
          sortable: true,

          label: this.$t("lab.normalRange"),
        },
        // {
        //   key: "result_as_to_normal_range",
        //   sortable: true,

        //   label: this.$t("lab.resultAsToNormalRange"),
        // },
        // {
        //   key: "discount",
        //   sortable: true,

        //   label: this.$t("common.discount"),
        // },
        // {
        //   key: "price_after_discount",
        //   sortable: true,

        //   label: this.$t("common.price"),
        // },
        { key: "Actions", label: this.$t("common.actions") },
        // { key: "state", label: this.$t("lab.approve_to_test_result") },
      ],
      Tests: {
        id: "Tests",
      },
      group: "",
      deleteForm: "",
      writeTest: "",
      selectedItem: null, // Store the selected item for approval/disapproval
      createMode: false,
      editSampleModal: false,
      currentSampleId: null,
      newSampleId: "",
      // Invoice related data
      testInvoices: [],
      loadingInvoices: false,
      invoiceSearchTerm: "",
      testNameSearchTerm: "",
      invoicePage: 1,
      invoiceTake: 20,
      invoiceTotalRecords: 0,
      testsData: [], // Store test data for mapping
      selectedInvoiceItems: [], // For modal display
      selectedInvoiceNo: "", // For modal title
      invoiceColumns: [
        {
          key: "invoice_no",
          sortable: true,
          label: this.$t("common.invoice-number"),
        },
        { key: "test_items", sortable: false, label: this.$t("lab.test-name") },
        { key: "discount", sortable: true, label: this.$t("common.discount") },
        { key: "amount", sortable: true, label: this.$t("common.amount") },
        { key: "status", sortable: true, label: this.$t("common.status") },
        { key: "date", sortable: true, label: this.$t("common.date") },
        { key: "approve_test", label: this.$t("lab.approve-to-test-result") },
      ],
    };
  },
  computed: {
    filteredTestInvoices() {
      let filtered = [...this.testInvoices];

      // Filter by invoice number
      if (this.invoiceSearchTerm) {
        const searchTerm = this.invoiceSearchTerm.toLowerCase();
        filtered = filtered.filter(
          (invoice) =>
            invoice.invoice_no &&
            invoice.invoice_no.toString().toLowerCase().includes(searchTerm)
        );
      }

      // Filter by test name from invoice_items
      if (this.testNameSearchTerm) {
        const searchTerm = this.testNameSearchTerm.toLowerCase();
        filtered = filtered.filter((invoice) => {
          const items = getNestedProperty(invoice, "invoice_items", []);
          return items.some((item) =>
            getNestedProperty(item, "name", "")
              .toLowerCase()
              .includes(searchTerm)
          );
        });
      }

      return filtered;
    },
    totalRequestedTests() {
      if (!this.testInvoices) return 0;
      return this.testInvoices.reduce((sum, invoice) => {
        const amount = getNestedProperty(invoice, "amount", 0);
        return sum + (parseFloat(amount) || 0);
      }, 0);
    },
    totalApprovedTests() {
      if (!this.testInvoices) return 0;
      return this.testInvoices
        .filter(
          (invoice) => getNestedProperty(invoice, "status") === "approved"
        )
        .reduce((sum, invoice) => {
          const amount = getNestedProperty(invoice, "amount", 0);
          return sum + (parseFloat(amount) || 0);
        }, 0);
    },
  },

  mounted() {
    this.getPatientInfo();
    this.getPatientsInfo();
    this.getTestInvoices();
  },

  methods: {
    getNestedProperty,

    // Show all test items in modal
    showAllTestItems(invoice) {
      this.selectedInvoiceItems = getNestedProperty(
        invoice,
        "invoice_items",
        []
      );
      this.selectedInvoiceNo = invoice.invoice_no || "";
      this.$bvModal.show("modal-all-test-items");
    },

    // Calculate total for modal
    calculateModalTotal() {
      return this.selectedInvoiceItems.reduce((sum, item) => {
        const amount = getNestedProperty(item, "net_amount", 0);
        return sum + (parseFloat(amount) || 0);
      }, 0);
    },

    formatCurrency(amount) {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "IQD",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(amount);
    },
    removeTest(index) {
      this.CreateForm.tests.splice(index, 1);
    },
    deleteModel(item) {
      this.deleteForm = item;
      this.$bvModal.show("modal-delete");
    },
    async getPatientsInfo() {
      const Id = this.$route.params.id;
      await this.getPatient(`/patientCard/${Id}`);
      for (const item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }

      this.patientInfo = this.all_data;
      console.log("this.patientInfo", this.patientInfo);
      this.dataCount = this.all_data.total;
    },
    async getPatientInfo() {
      const Id = this.$route.params.id;

      await this.getPatient(
        `patientCardTest?filter[patient_card_id]=${Id}&include=test,unit&sort=-id&take=1000`
      );
      this.loading = false;
      for (const item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }

      this.data = this.all_data.data;
      console.log("this.data", this.data);
      // const dataWithNormalRange = await Promise.all(
      //   this.data.map(async (patientTest) => {
      //     // console.log("patientTest", patientTest.test_id);
      //     const normalRange = await this.getNormalRange(patientTest.test_id);
      //     // console.log("normalRange", normalRange);
      //     return {
      //       ...patientTest,
      //       test: normalRange,
      //     };
      //   })
      // );
      // this.data = dataWithNormalRange;

      // console.log(
      //   "this.data.test.normal_ranges",
      //   this.data[0].test
      // );
      // this.data.map((item) => {
      //   const writeTest = this.getPatientNormalRangeByAgeAndGender(
      //     this.calculateAge(this.patientInfo.patient.birth_date),
      //     this.patientInfo.patient.gender,
      //     item.test.normal_ranges
      //   );
      //   // console.log("writeTest", writeTest);
      //   item.writeTest = writeTest;
      // });

      console.log("this.data", this.data);
    },
    async getNormalRange(testId) {
      await this.getPatient(`/test/${testId}?include=normalRanges`);
      const testWithNormalRange = this.all_data;
      // console.log("testWithNormalRange", testWithNormalRange);
      return testWithNormalRange;
    },
    getPatientNormalRangeByAgeAndGender(
      patientAge,
      patientGender,
      normalRanges
    ) {
      let writeTest = null;
      if (Array.isArray(normalRanges)) {
        normalRanges.map((normalRange) => {
          if (
            normalRange.min_age <= patientAge &&
            normalRange.max_age >= patientAge &&
            normalRange.gender == patientGender
          ) {
            writeTest = normalRange;
          }
        });
      } else {
        console.error("normalRanges is not an array:", normalRanges);
      }
      console.log("writeTest", writeTest);
      return writeTest;
    },
    calculateAge(birth_date) {
      // birth_date = "1950-01-01";
      const today = new Date();
      const birthDate = new Date(birth_date);
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (
        monthDiff < 0 ||
        (monthDiff === 0 && today.getDate() < birthDate.getDate())
      ) {
        age--;
      }
      console.log("age", age);
      return age;
    },
    addDiscount() {
      (this.params.form = this.EditForm),
        (this.params.path = `/patientCardTest/${this.EditForm.id}`),
        this.$store
          .dispatch("update", this.params)
          .then((response) => {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Patient Card Test Added",
                variant: "success",
              },
            });
            console.log("joo");
            this.getPatientInfo();
          })
          .catch((error) => {
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
    },
    submitItem() {
      for (const item in this.errorForm) {
        this.errorForm[item] = "";
        if (this.EditForm[item] == "" || this.EditForm[item] == null)
          delete this.EditForm[item];
      }

      if (this.createMode) {
        (this.params.form = this.EditForm),
          (this.params.path = "/patientCardTest"),
          this.$store
            .dispatch("create", this.params)
            .then((response) => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: "Patient Card Radiology Added",
                  variant: "success",
                },
              });
              this.getPatientInfo();
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      } else {
        (this.params.form = this.EditForm),
          (this.params.path = `/patientCardTest/${this.EditForm.id}`),
          this.$store
            .dispatch("update", this.params)
            .then((response) => {
              this.$toast({
                component: ToastificationContent,
                position: "top-right",
                props: {
                  title: "Patient Card Test Added",
                  variant: "success",
                },
              });
              console.log("joo");
              this.getPatientInfo();
            })
            .catch((error) => {
              if (error.response.status === 400) {
                for (const item in error.response.data) {
                  this.$toast({
                    component: ToastificationContent,
                    position: "top-right",
                    props: {
                      title: "validation error",
                      variant: "danger",
                      text: error.response.data[item][0],
                    },
                  });
                }
              }
            });
      }
    },
    show(item, mode) {
      if (mode === "create") {
        const Id = this.$route.params.id;
        this.createMode = true;
        this.CreateForm.patient_card_id = Id;

        this.$bvModal.show("modal-add-test");
      } else {
        this.createMode = false;

        this.EditForm.id = item.item.id;
        this.EditForm.normalRange = item.item.normalRange;
        this.EditForm.patient_card_id = item.item.patient_card_id;
        this.EditForm.price = item.item.price;
        this.EditForm.result = item.item.result;
        this.EditForm.unit_id = item.item.unit_id;
        this.EditForm._method = "PUT";
        this.$bvModal.show("modal-radiology-edit");
      }
    },
    showDiscount(item) {
      this.EditForm.id = item.item.id;
      this.EditForm.discount = item.item.discount;
      this.EditForm.normalRange = item.item.normalRange;
      this.EditForm.patient_card_id = item.item.patient_card_id;
      this.EditForm.price = item.item.price;
      this.EditForm.result = item.item.result;
      this.EditForm.unit_id = item.item.unit_id;

      this.EditForm._method = "PUT";
      this.$bvModal.show("modal-add-discount");
    },
    async selectGroup(e) {
      await this.getPatient(`/testGroup?filter[group_id]=${e}&include=test`);
      for (const item in this.all_data) {
        if (this.all_data[item] == null) this.all_data[item] = "";
      }

      this.CreateForm.tests = this.all_data.data;
    },
    async addTest() {
      (this.params.form = this.CreateForm),
        (this.params.path = "/patientCardTest"),
        this.$store
          .dispatch("create", this.params)
          .then((response) => {
            this.$toast({
              component: ToastificationContent,
              position: "top-right",
              props: {
                title: "Patient Card Test Added",
                variant: "success",
              },
            });

            this.getPatientInfo();
          })
          .catch((error) => {
            console.log(error);
            if (error.response.status === 400) {
              for (const item in error.response.data) {
                this.$toast({
                  component: ToastificationContent,
                  position: "top-right",
                  props: {
                    title: "validation error",
                    variant: "danger",
                    text: error.response.data[item][0],
                  },
                });
              }
            }
          });
    },
    deleteTest() {
      this.$store
        .dispatch("delete", `/patientCardTest/${this.deleteForm.id}`)
        .then((response) => {
          this.getPatientInfo();

          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Delete Success",
              icon: "success",
              variant: "info",
              text: `deleted ${this.deleteForm.first_name} successfully`,
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: "Error",
              variant: "danger",
              text: `Error deleting ${this.deleteForm.first_name}`,
            },
          });
        });
    },
    showApproveModal(item) {
      this.selectedItem = item;
      this.$bvModal.show("approve-modal");
    },
    showDisapproveModal(item) {
      this.selectedItem = item;
      this.$bvModal.show("disapprove-modal");
    },
    async approveTest() {
      try {
        // Call your API to update is_approved to 1
        await axios.put(`/patientCardTest/${this.selectedItem.id}`, {
          is_approved: 1,
        });
        // Update the local state
        await this.getPatientInfo();
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Test Approved",
            message: `${this.selectedItem.test.name} approved successfully`,
            variant: "success",
          },
        });
      } catch (error) {
        console.error("Error approving test:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Failed to approve test",
            message: `${error.response.data.message}`,
            variant: "danger",
          },
        });
      } finally {
        this.selectedItem = null; // Reset the selected item
      }
    },
    async disapproveTest() {
      try {
        // Call your API to update is_approved to 0
        await axios.put(`/patientCardTest/${this.selectedItem.id}`, {
          is_approved: 0,
        });
        // Update the local state
        await this.getPatientInfo();
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: "Test Disapproved",
            variant: "danger",
          },
        });
      } catch (error) {
        console.error("Error disapproving test:", error);
        this.$toast.error("Failed to disapprove test.");
      } finally {
        this.selectedItem = null; // Reset the selected item
      }
    },
    editSampleId(item) {
      this.currentSampleId = item;
      // Get current sample ID if needed or leave blank for user to enter new one
      this.editSampleModal = true;
    },

    updateSampleId() {
      // Replace with your actual API call
      this.$http
        .put(`/patientCardTest/${this.currentSampleId.id}`, {
          sampleId: this.newSampleId,
        })
        .then(() => {
          this.editSampleModal = false;
          // Refresh the data or update it locally
          this.refreshData();
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Success",
              variant: "success",
              text: "Sample ID updated successfully",
            },
          });
        })
        .catch((error) => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: "Error",
              variant: "danger",
              text: "Failed to update Sample ID",
            },
          });
          console.error(error);
        });
    },

    // Tab change handler
    onTabChanged(tabIndex) {
      if (tabIndex === 0) {
        // Patient Tests tab - refresh patient tests data
        this.getPatientInfo();
      } else if (tabIndex === 1) {
        // Test Invoices tab - refresh invoice data
        this.getTestInvoices();
      }
    },

    // Invoice related methods
    async getTests() {
      try {
        await this.getPatient("/test?take=1000");
        this.testsData = this.all_data?.data || [];
      } catch (error) {
        console.error("Error fetching tests:", error);
      }
    },

    async getTestInvoices() {
      try {
        this.loadingInvoices = true;
        const patientCardId = this.$route.params.id;

        // First, get all tests data
        await this.getTests();

        // Fetch all invoices for this patient card (no search filters)
        const url = `/invoices?take=1000&filter[patient_card_id]=${patientCardId}&filter[type]=invoice_patient_card_test&include=invoiceItems.itemable`;

        await this.getPatient(url);

        const rawInvoices = this.all_data?.data || [];

        // Use invoices directly with invoice_items
        this.testInvoices = rawInvoices;

        this.invoiceTotalRecords = this.testInvoices.length;
      } catch (error) {
        console.error("Error fetching test invoices:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("common.error-loading-invoices"),
          },
        });
      } finally {
        this.loadingInvoices = false;
      }
    },

    // Check if all tests in invoice are approved
    areAllTestsApproved(invoice) {
      const items = getNestedProperty(invoice, "invoice_items", []);
      if (items.length === 0) return false;

      return items.every((item) => {
        const isApproved = getNestedProperty(item, "itemable.is_approved", 0);
        return isApproved === 1;
      });
    },

    // Approve all tests from invoice
    async approveTestFromInvoice(invoice) {
      try {
        const items = getNestedProperty(invoice, "invoice_items", []);

        if (items.length === 0) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("lab.test-not-found"),
            },
          });
          return;
        }

        // Loop through all invoice items and approve each test
        const approvalPromises = items.map((item) => {
          const patientCardTestId = getNestedProperty(item, "itemable.id");
          if (patientCardTestId) {
            return axios.put(`/patientCardTest/${patientCardTestId}`, {
              is_approved: 1,
            });
          }
        });

        await Promise.all(approvalPromises.filter(Boolean));

        // Show success message
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("lab.test-approved"),
            variant: "success",
            text: this.$t("lab.test-approved-successfully"),
          },
        });

        // Refresh both tabs to show updated data
        this.getPatientInfo();
        this.getTestInvoices();
      } catch (error) {
        console.error("Error approving test:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("lab.error-approving-test"),
          },
        });
      }
    },

    // Disapprove all tests from invoice
    async disapproveTestFromInvoice(invoice) {
      try {
        const items = getNestedProperty(invoice, "invoice_items", []);

        if (items.length === 0) {
          this.$toast({
            component: ToastificationContent,
            position: "top-right",
            props: {
              title: this.$t("common.error"),
              variant: "danger",
              text: this.$t("lab.test-not-found"),
            },
          });
          return;
        }

        // Loop through all invoice items and disapprove each test
        const disapprovalPromises = items.map((item) => {
          const patientCardTestId = getNestedProperty(item, "itemable.id");
          if (patientCardTestId) {
            return axios.put(`/patientCardTest/${patientCardTestId}`, {
              is_approved: 0,
            });
          }
        });

        await Promise.all(disapprovalPromises.filter(Boolean));

        // Show success message
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("lab.test-disapproved"),
            variant: "success",
            text: this.$t("lab.test-disapproved-successfully"),
          },
        });

        // Refresh both tabs to show updated data
        this.getPatientInfo();
        this.getTestInvoices();
      } catch (error) {
        console.error("Error disapproving test:", error);
        this.$toast({
          component: ToastificationContent,
          position: "top-right",
          props: {
            title: this.$t("common.error"),
            variant: "danger",
            text: this.$t("lab.error-disapproving-test"),
          },
        });
      }
    },

    formatPrice(price) {
      return price ? `${Number(price).toLocaleString()} IQD` : "0 IQD";
    },

    formatDate(date) {
      if (!date) return this.$t("common.not-available");
      return new Date(date).toLocaleDateString();
    },

    formatDateTime(dateTime) {
      if (!dateTime) return this.$t("common.not-available");
      return `${new Date(dateTime).toLocaleDateString()} ${new Date(
        dateTime
      ).toLocaleTimeString()}`;
    },

    getInvoiceStatusVariant(status) {
      switch (status) {
        case "approved":
          return "success";
        case "pending":
          return "warning";
        case "rejected":
          return "danger";
        case "printed":
          return "info";
        default:
          return "secondary";
      }
    },
  },
};
</script>

<style scoped>
.mb-md-0 {
  margin-top: 8px;
}

table {
  width: 94%;
  border: 1px solid;
  font-size: 11px;
}

.no-border {
  border: 0;
}

.square-dark {
  background-color: rgba(0, 0, 0, 0.2);
}

tr,
th,
td {
  border: 1px solid;
  width: 10px;
  height: 10px;
  text-align: center;
}

.font-modify {
  letter-spacing: normal !important;
}

.pdf-table {
  page-break-inside: avoid;
}

.square {
  width: 30px;
  /* Smaller width for squares */
  height: 30px;
  /* Smaller height for squares */
  background-color: #000;
  /* Color of the square */
  display: inline-block;
  /* Allows us to set width and height */
}

.centered-text {
  text-align: center;
  border: 2px solid #000;
  /* Border for the cell */
}

#table-above {
  border-collapse: collapse;
  /* Removes space between borders */
  font-size: 12px;
  /* Smaller font size for the entire table */
}

@media print {
  @page {
  }

  .pdf-table {
    page-break-before: avoid;
    page-break-inside: avoid;
  }
}

.pdf-table:first-child {
  margin-top: 1em;
  /* Adjust the value as needed */
  page-break-inside: avoid;
}

/* Test Items Container Styling */
.test-items-container {
  min-width: 250px;
  background-color: #f8f9fa;
  border-radius: 6px;
  padding: 8px;
  border: 1px solid #e9ecef;
}

.test-item-row {
  padding: 6px 0;
}

.test-item-row.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}

.test-item-name {
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
}

.test-item-amount {
  margin-left: 12px;
  white-space: nowrap;
}

.badge-light-primary {
  /* background-color: rgba(115, 103, 240, 0.12); */
  /* color: #7367f0; */
  padding: 4px 8px;
  border-radius: 4px;
  font-weight: 600;
}

/* Modal test items styling */
.test-item-modal-row {
  padding: 8px 0;
}

.test-item-modal-row.border-bottom {
  border-bottom: 1px solid #e9ecef !important;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
